import { add, qs, qsa, remove, listen } from "@fluorescent/dom";
import { emit } from "@/glow/events";
import { cart } from "@/glow";
import { userIsTabbing } from "@/lib/a11y";
import "@/lib/product-item-video";
import "@/lib/product-item-gallery";

const selectors = {
  item: ".product-item",
  itemInner: ".product-item__inner",
  quickAddButton: '[data-quick-shop-trigger="quick-add"]',
  quickViewButton: '[data-quick-shop-trigger="quick-view"]',
  quickCart: ".quick-cart",
  purchaseConfirmation: ".purchase-confirmation-popup",
};

export default function ProductItem(container) {
  const items = qsa(selectors.item, container);
  if (!items.length) return;

  // Add z-index for quick-buy overlap
  items.forEach((item, i) =>
    item.style.setProperty("--z-index-item", items.length - i)
  );

  const quickAddButtons = qsa(selectors.quickAddButton, container);
  const quickViewButtons = qsa(selectors.quickViewButton, container);
  const quickCart = qs(selectors.quickCart, document);
  const purchaseConfirmation = qs(selectors.purchaseConfirmation, document);

  const events = [
    listen(quickAddButtons, "click", e => {
      const buttonEl = e.currentTarget;
      add(buttonEl, "loading");

      // if quick cart and confirmation popup are disabled, use standard form submit
      if (!purchaseConfirmation && !quickCart) return;

      e.preventDefault();
      e.stopPropagation();

      const { productId } = buttonEl.dataset;
      if (!productId) return;

      const minQuantity = buttonEl.getAttribute("data-quantity-minimum");

      cart.addItemById(productId, minQuantity).then(
        ({ res }) => {
          remove(buttonEl, "loading");

          if (purchaseConfirmation && !userIsTabbing()) {
            emit("confirmation-popup:open", null, { product: res.items[0] });
          } else {
            emit("quick-cart:updated");
            // Need a delay to allow quick-cart to refresh
            setTimeout(() => {
              emit("quick-cart:open");
            }, 300);
          }
        },
        error => {
          console.error(error);
          remove(buttonEl, "loading");
          alert(error.message);
          emit("quick-cart:open");
        }
      );
    }),

    listen(quickViewButtons, "click", e => {
      e.preventDefault();
      e.stopPropagation();
      const buttonEl = e.currentTarget;
      const { productUrl } = buttonEl.dataset;
      if (!productUrl) return;

      emit("quick-view:open", null, {
        productUrl: productUrl,
      });
    }),
  ];

  const unload = () => {
    events.forEach(unsubscribe => unsubscribe());
  };

  return { unload };
}

class ProductItemSwatchesList extends HTMLElement {
  static observedAttributes = ["data-active-url"];

  constructor() {
    super();
    this.selectors = {
      optionSwatch: "[js-option-swatch]",
      optionChip: "[js-option-chip]",
      optionChipWithValue: value =>
        `${this.selectors.optionChip}[data-option-value="${value}"]`,
      productItem: "[data-product-handle]",
      productItemGallery: "product-item-gallery",
      productItemSwatchImage: "[js-product-item-swatch-image]",
      quickViewButton: "[data-quick-shop-trigger]",
      variantJSON: "[data-variant-json]",
    };

    this.attrs = {
      activeURL: "data-active-url",
      optionIndex: "data-option-index",
      optionValue: "data-option-value",
      productHandle: "data-product-handle",
      quickViewButtonURL: "data-product-url",
      selectedVariantMediaId: "data-selected-variant-media-id",
      variantMediaId: "data-variant-media-id",
    };

    this.classes = {
      active: "active",
      disabled: "disabled",
      selected: "selected",
    };
  }

  connectedCallback() {
    this.eventController = new AbortController();
    this.eventOptions = { signal: this.eventController.signal };

    this.productItem = this.closest("[data-product-handle]");
    this.quickViewButtons = this.productItem.querySelectorAll(this.selectors.quickViewButton);

    this.productItemGallery = this.productItem.querySelector(
      this.selectors.productItemGallery
    );
    this.productHandle = this.productItem.getAttribute(
      this.attrs.productHandle
    );
    this.variantJSONEl = this.productItem.querySelector(
      this.selectors.variantJSON
    );

    if (this.variantJSONEl) {
      if (!this.variantJSONEl.innerHTML.trim()) return null;

      this.variantJSON = JSON.parse(this.variantJSONEl.innerHTML);
    }

    this.productItemSwatchImages = this.productItem.querySelectorAll(
      this.selectors.productItemSwatchImage
    );
    this.optionSwatches = this.querySelectorAll(this.selectors.optionSwatch);
    this.optionSwatches.forEach(swatch => {
      swatch.addEventListener(
        "click",
        e => this.handleOptionClick(e),
        this.eventOptions
      );
    });

    import(flu.chunks.swiper).then(
      ({ Swiper, Navigation, A11y, Mousewheel }) => {
        this.swiper = new Swiper(this, {
          modules: [Navigation, A11y, Mousewheel],
          navigation: {
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
          },
          a11y: {
            enabled: true,
            scrollOnFocus: true,
          },
          mousewheel: {
            enabled: true,
            forceToAxis: true,
          },
          slidesPerView: "auto",
          slidesPerGroupAuto: true,
          spaceBetween: 6,
          watchOverflow: true,
          on: {
            lock: () => {
              this.classList.add("swiper-is-locked");
            },
            unlock: () => {
              this.classList.remove("swiper-is-locked");
            },
          },
        });
      }
    );
  }

  attributeChangedCallback(name, _oldValue, newValue) {
    switch (name) {
      case "data-active-url":
        if (!newValue) break;
        if (this.quickViewButtons.length !== 0) {
          this.quickViewButtons.forEach(button => {
            button.setAttribute(this.attrs.quickViewButtonURL, newValue);
          });
        }

        break;
    }
  }

  handleOptionClick(event) {
    const targetOptionValue = event.target.getAttribute(this.attrs.optionValue);
    const targetOptionIndex = event.target.getAttribute(this.attrs.optionIndex);
    const targetVariantMediaId = event.target.getAttribute(
      this.attrs.variantMediaId
    );
    this.optionSwatches.forEach(swatch =>
      swatch.classList.toggle(this.classes.selected, false)
    );
    event.target.classList.toggle(this.classes.selected, true);

    // if Dynamic option availability is enabled
    if (this.variantJSON) {
      const valuesToManage = this.createAvailableOptionsTree(this.variantJSON, [
        { value: targetOptionValue, index: `option${targetOptionIndex}` },
      ]);

      for (const [option, values] of Object.entries(valuesToManage)) {
        this.manageOptionState(option, values);
      }
    }

    this.switchActiveSwatchImage(targetOptionValue);
    this.productItemGallery?.setAttribute(
      this.attrs.selectedVariantMediaId,
      targetVariantMediaId
    );
  }

  // Create a list of all options. If any variant exists and is in stock with that option, it's considered available
  createAvailableOptionsTree(variants, currentlySelectedValues) {
    // Reduce variant array into option availability tree
    return variants.reduce(
      (options, variant) => {
        // Check each option group (e.g. option1, option2, option3) of the variant
        Object.keys(options).forEach(index => {
          if (variant[index] === null) return;

          let entry = options[index].find(
            option => option.value === variant[index]
          );

          if (typeof entry === "undefined") {
            // If option has yet to be added to the options tree, add it
            entry = { value: variant[index], soldOut: true };
            options[index].push(entry);
          }

          const currentOption1 = currentlySelectedValues.find(
            ({ index }) => index === "option1"
          );

          switch (index) {
            case "option1":
              // Option1 inputs should always remain enabled based on all available variants
              entry.soldOut =
                entry.soldOut && variant.available ? false : entry.soldOut;
              break;
            case "option2":
              // Option2 inputs should remain enabled based on available variants that match first option group
              if (currentOption1 && variant.option1 === currentOption1.value) {
                entry.soldOut =
                  entry.soldOut && variant.available ? false : entry.soldOut;
              }
              break;
          }
        });

        return options;
      },
      { option1: [], option2: [] }
    );
  }

  manageOptionState(option, values) {
    // Loop through each option value
    values.forEach(obj => {
      this.toggleVariantOption(obj);
    });
  }

  toggleVariantOption(obj) {
    // Selecting by value so escape it
    const value = this.escapeQuotes(obj.value);
    const chipWithValue = this.productItem.querySelector(
      this.selectors.optionChipWithValue(value)
    );
    chipWithValue?.classList.toggle(this.classes.disabled, obj.soldOut);
  }

  switchActiveSwatchImage(targetOptionValue) {
    this.productItemSwatchImages.forEach(swatchImage => {
      if (swatchImage.dataset.swatchValue === targetOptionValue) {
        swatchImage.classList.add(this.classes.active);
        this.setAttribute(this.attrs.activeURL, swatchImage.href);
      } else {
        swatchImage.classList.remove(this.classes.active);
      }
    });
  }

  escapeQuotes(str) {
    const escapeMap = {
      '"': '\\"',
      "'": "\\'",
    };

    return str.replace(/"|'/g, m => escapeMap[m]);
  }

  disconnectedCallback() {
    this.eventController.abort();
  }
}

if (!customElements.get("product-item-swatches-list")) {
  customElements.define("product-item-swatches-list", ProductItemSwatchesList);
}
